import type { ReactNativeOptions } from '@sentry/react-native';
import { Platform } from 'react-native';
import { NetworkError, PermissionDeniedError, ResourceNotFoundError } from './errors';

/**
 * Sentry noise controls. Everything here was derived from a 90-day audit of
 * the production project (Sept 2026): ~60% of events were handled network
 * failures re-logged as errors, ~13% came from browser extensions and
 * browser-injected scripts. See lib/logger.ts for the level demotion side.
 */

// ---------------------------------------------------------------------------
// Error classification (shared with lib/logger.ts)
// ---------------------------------------------------------------------------

/**
 * Messages browsers/SDKs produce when a request never reached the server or
 * was cut off: offline, tab sleeping, user navigated away, ad-blocker, or a
 * CORS-less error response. None of these are actionable app bugs.
 */
export const NETWORK_ERROR_MESSAGE_RE =
  /Failed to fetch|Load failed|NetworkError when attempting to fetch|Network request failed|Network request timed out|A network error (has )?occurred|network connection was lost|Could not connect to the server|Failed to send a request to the Edge Function|signal is aborted|Fetch is aborted|The operation was aborted|The user aborted a request|fetching process for the media resource was aborted|No response received/i;

const NETWORK_ERROR_NAMES = new Set([
  'AbortError',
  'NetworkError',
  'TimeoutError',
  'FunctionsFetchError',
  'AuthRetryableFetchError',
]);

/**
 * True for Error instances, Supabase/PostgREST error objects, or strings that
 * describe a transport failure rather than an application error.
 */
export function isNetworkErrorLike(value: unknown): boolean {
  if (!value) return false;
  if (typeof value === 'string') return NETWORK_ERROR_MESSAGE_RE.test(value);
  if (typeof value !== 'object') return false;
  if (value instanceof NetworkError) return true;
  const v = value as { name?: unknown; message?: unknown };
  if (typeof v.name === 'string' && NETWORK_ERROR_NAMES.has(v.name)) return true;
  if (typeof v.message === 'string' && NETWORK_ERROR_MESSAGE_RE.test(v.message)) return true;
  return false;
}

/**
 * Errors the app already handles with dedicated UI (paywall, 404 screen,
 * "check your connection" toast). They are useful as breadcrumbs but should
 * never open a Sentry issue on their own.
 */
export function isExpectedAppError(value: unknown): boolean {
  if (value instanceof PermissionDeniedError) return true;
  if (value instanceof ResourceNotFoundError) return true;
  return isNetworkErrorLike(value);
}

// ---------------------------------------------------------------------------
// Sentry.init options
// ---------------------------------------------------------------------------

/**
 * Errors thrown by code we do not ship: browser internals, extensions,
 * in-app browsers, Google Translate, and the expo-font web loader's
 * unhandled font timeout. Each pattern maps to a confirmed Sentry issue.
 */
export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = [
  // Safari 26 WebKit media-controls script (REACT-NATIVE-BV, 17k events)
  /Can't find variable: EmptyRanges/,
  // Chrome/Edge extension wrapping fetch (REACT-NATIVE-PD and ~20 siblings)
  /Cannot read properties of undefined \(reading 'M_ID'\)/,
  // expo-font web: fontfaceobserver rejects after 6s on slow connections; the
  // font still applies when it arrives and useFonts already tolerates it.
  /^\d+ms timeout exceeded$/,
  // Microsoft Outlook SafeLinks / Editor extension
  /Object Not Found Matching Id:\d+, MethodName:/,
  /Non-Error promise rejection captured with value: undefined/,
  /Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection/,
  // Crypto wallet extensions
  /'set' on proxy: trap returned falsish for property '(tronlinkParams|defaultAddress)'/,
  /Failed to connect to MetaMask|MetaMask extension not found/,
  /Cannot redefine property: ethereum/,
  // Safari / Firefox iOS extensions and reader mode
  /Can't find variable: (observer|__firefox__|output)/,
  /window\.__firefox__/,
  /webkit\.messageHandlers/,
  /Invalid call to runtime\.sendMessage\(\)/,
  /No Listener: tabs:outgoing/,
  /Zotero Connector/,
  /contentScriptData\.init_ts/,
  /getUrlAutofillTargetingRules/,
  /Invalid call to browser\.storage/,
  /Permission denied to access property "force"/,
  /Error invoking postEvent/,
  /can't access dead object/,
  // Android/iOS in-app browsers (Facebook, Instagram, Gmail)
  /Error invoking postMessage: Java object is gone/,
  /WKWebView API client did not respond|WKWebView was deallocated/,
  // Safari native media track picker
  /^Type error$/,
  // Google Translate (Closure-compiled): two-letter error names
  /^[A-Za-z]{2}(`prod)?$/,
  // Third-party widgets (HelpScout Beacon ships axios; the app does not)
  /^AxiosError: /,
  // Mixpanel blocked by an ad-blocker; the SDK already degrades gracefully
  /Failed to fetch \(api\.mixpanel\.com\)/,
];

/**
 * Web only: keep events whose top frame comes from our Expo bundle. Anything
 * else (chrome-extension://, page-URL filenames from Chrome-iOS injected
 * scripts, HelpScout's vendor bundle) is dropped before it leaves the browser.
 * Must NOT be applied on native, where frames point at main.jsbundle.
 */
export const SENTRY_WEB_ALLOW_URLS: (string | RegExp)[] = [/\/_expo\/static\/js\/web\//];

type BeforeSend = NonNullable<ReactNativeOptions['beforeSend']>;

const NON_LOCATABLE_FILENAMES = new Set(['', 'undefined', 'null', '<anonymous>', '[native code]', 'native']);

function isLocatableFrame(filename: string | undefined): boolean {
  if (!filename) return false;
  const bare = filename.replace(/^app:\/\/\/?/, '');
  return !NON_LOCATABLE_FILENAMES.has(bare);
}

/**
 * Web only: drop *unhandled* errors whose stack has frames but none with a
 * real filename. That shape only comes from browser-injected scripts (Safari
 * media controls, Chrome-iOS helpers) and cannot be symbolicated or fixed.
 * Handled errors and frameless events pass through untouched.
 */
export const sentryBeforeSend: BeforeSend = (event) => {
  if (Platform.OS !== 'web') return event;
  const values = event.exception?.values ?? [];
  if (values.length === 0) return event;
  if (values.some((v) => v.mechanism?.handled !== false)) return event;
  const frames = values.flatMap((v) => v.stacktrace?.frames ?? []);
  if (frames.length === 0) return event;
  return frames.some((f) => isLocatableFrame(f.filename)) ? event : null;
};
